import { useOktaAuth } from '@okta/okta-react';
import { DmsAnalytics, DmsAnalyticsInterface } from 'dms-analytics';
import { createContext, useContext, useState, useEffect } from 'react';
import { APP_ENV } from 'adapters/constants';
import { REACT_APP_AMPLITUDE_API_KEY } from 'common/constants';
import { useClient } from 'components/ClientProvider';
import { getDepartmentId, getStoreName } from 'utils/getClientInfo';

type ContextValue = DmsAnalyticsInterface | null;
const AmplitudeContext = createContext<ContextValue>(null);

export function AmplitudeProvider({
  children
}: {
  children: React.ReactNode;
}): React.JSX.Element {
  const okta = useOktaAuth();
  const [userId, setUserId] = useState<string>('');
  const [apiKey, setApiKey] = useState('');
  const [amplitudeInstance, setAmplitudeInstance] =
    useState<DmsAnalyticsInterface>(new DmsAnalytics(apiKey, userId));
  const userGuid = okta?.authState?.accessToken?.claims?.userGuid as string;
  const client = useClient();

  useEffect(() => {
    if (client && userGuid && userGuid !== userId) setUserId(userGuid);
  }, [userId, client, userGuid]);

  const amplitudeApiKey = REACT_APP_AMPLITUDE_API_KEY[APP_ENV];

  useEffect(() => {
    if (amplitudeApiKey && amplitudeApiKey !== apiKey)
      setApiKey(amplitudeApiKey);
  }, [apiKey, amplitudeApiKey]);

  useEffect(() => {
    if (
      client?.customClientValues &&
      userId !== '' &&
      apiKey !== '' &&
      amplitudeApiKey
    ) {
      const { 'x-enterprise-id': enterpriseId, 'x-store-id': storeId } =
        client.headers;
      const storeName = getStoreName(
        client?.customClientValues?.stores,
        storeId
      );
      const departmentId = getDepartmentId(
        client?.customClientValues?.departmentIds,
        storeId
      );
      const amp = new DmsAnalytics(amplitudeApiKey);
      const identifyObj = new amp.amplitude.Identify();
      identifyObj.set('Enterprise ID', enterpriseId ?? '');
      identifyObj.set('Store ID', storeId ?? '');
      identifyObj.set('Roles', client?.customClientValues?.roles);
      identifyObj.set('Store Name', storeName ?? '');
      identifyObj.set('Department ID', departmentId ?? '');
      amp.amplitude.identify(identifyObj);
      setAmplitudeInstance(amp);
    }
  }, [
    apiKey,
    userId,
    client?.customClientValues,
    client?.headers,
    amplitudeApiKey
  ]);

  return (
    <AmplitudeContext.Provider
      value={userId !== '' && apiKey !== '' ? amplitudeInstance : null}
    >
      {children}
    </AmplitudeContext.Provider>
  );
}

export const useAmplitude = (): ContextValue => {
  const context = useContext(AmplitudeContext);
  return context;
};
